/**
 * SOUND-599 — chart shaping for the Auth Performance report builder.
 *
 * Pure. No React, no Recharts, no formatting. Everything here is about ONE problem:
 *
 *   A chart AGGREGATES BY DEFINITION. The report can be grouped by up to 6
 *   dimensions, but a chart shows an x-axis plus at most one series — so every
 *   chart collapses rows, and every collapse is a re-aggregation.
 *
 * `attempts` and `approved` are additive. `auth_rate` is a RATIO and is not.
 * Averaging a set of rates gives a number that is wrong in a way nobody notices,
 * which is the same trap as storing a rate in the mart — just moved into the view
 * layer. So the rule, enforced here rather than documented and hoped for:
 *
 *   Every collapse sums attempts and approved, then recomputes
 *   rate = 100 * Σapproved / Σattempts. Including the "Other" bucket.
 *
 * The corollaries are the chart-type gates: a rate cannot be stacked (the stack
 * total is meaningless) and cannot be a donut (slices don't sum to a whole).
 */

export const OTHER = "Other";

// Ranking is ALWAYS by attempts, whatever measure is displayed. Volume is what makes
// a group matter; picking the top 10 by auth_rate would surface the thinnest groups,
// since a single approved attempt is 100%.
export const RANK_BY = "attempts";

export const MEASURES = [
  { value: "auth_rate", label: "Auth Rate", additive: false, unit: "percent" },
  { value: "attempts", label: "Attempts", additive: true, unit: "count" },
  { value: "approved", label: "Approved", additive: true, unit: "count" },
];

const MEASURE_BY_VALUE = Object.fromEntries(MEASURES.map((m) => [m.value, m]));

export const isAdditive = (measure) => MEASURE_BY_VALUE[measure]?.additive === true;
export const measureLabel = (measure) => MEASURE_BY_VALUE[measure]?.label || measure;
export const measureUnit = (measure) => MEASURE_BY_VALUE[measure]?.unit || "count";

export const CHART_TYPES = [
  { value: "line", label: "Line" },
  { value: "combo", label: "Line + volume" },
  { value: "bar", label: "Bar" },
  { value: "donut", label: "Donut" },
];

/**
 * Which chart types are legal for the current report shape, and why not.
 *
 * `dateDims` is the set of groupBy keys that are calendar dates — passed in rather
 * than hardcoded so this module stays independent of the page's DIMENSIONS table.
 */
export function chartCapability({ groupBy = [], measure = "auth_rate", dateDims = new Set() }) {
  const dims = groupBy.length;
  const hasDate = groupBy.some((k) => dateDims.has(k));
  const additive = isAdditive(measure);

  const reason = (type) => {
    switch (type) {
      case "line":
      case "combo":
        if (!hasDate) return "Needs a date dimension — group by Date (UTC) or Date (ET) for a time series.";
        return null;
      case "bar":
        if (!dims) return "Group by at least one dimension.";
        return null;
      case "donut":
        if (dims !== 1) return "Needs exactly one dimension.";
        if (!additive) return `${measureLabel(measure)} is a ratio — slices would not sum to a whole. Pick Attempts or Approved.`;
        return null;
      default:
        return "Unknown chart type.";
    }
  };

  const capability = {};
  for (const t of CHART_TYPES) {
    const why = reason(t.value);
    capability[t.value] = { available: why === null, reason: why };
  }
  return capability;
}

/** Stacking sums the series at each x — meaningless for a ratio, and needs a series. */
export function canStack({ groupBy = [], measure = "auth_rate" }) {
  if (!isAdditive(measure)) {
    return { available: false, reason: `${measureLabel(measure)} is a ratio — a stack total would be meaningless.` };
  }
  if (groupBy.length < 2) {
    return { available: false, reason: "Needs a second dimension to stack by." };
  }
  return { available: true, reason: null };
}

/**
 * Pick a valid chart type, preferring what the user asked for.
 * Returns what changed so the UI can say why rather than rendering an empty frame.
 */
export function resolveChartType({ requested, groupBy = [], measure = "auth_rate", dateDims = new Set() }) {
  const capability = chartCapability({ groupBy, measure, dateDims });
  if (requested && capability[requested]?.available) {
    return { type: requested, fellBack: false, reason: null, capability };
  }
  // Preference order: a time series when a date is grouped, else bars.
  const order = ["line", "bar", "donut"];
  const fallback = order.find((t) => capability[t]?.available) || null;
  return {
    type: fallback,
    fellBack: Boolean(requested) && fallback !== requested,
    reason: requested ? capability[requested]?.reason || null : null,
    capability,
  };
}

/** Default axis assignment: date on x if there is one, then the next dim as series. */
export function defaultAxes({ groupBy = [], dateDims = new Set() }) {
  if (!groupBy.length) return { xDim: null, seriesDim: null };
  const xDim = groupBy.find((k) => dateDims.has(k)) || groupBy[0];
  const seriesDim = groupBy.find((k) => k !== xDim) || null;
  return { xDim, seriesDim };
}

const rate = (attempts, approved) => (attempts > 0 ? (100 * approved) / attempts : null);
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** A cell's displayed value under the chosen measure. NEVER a stored/averaged rate. */
function cellValue({ attempts, approved }, measure) {
  if (measure === "attempts") return attempts;
  if (measure === "approved") return approved;
  return rate(attempts, approved); // null when attempts === 0 → a gap, not 0%
}

/**
 * Collapse report rows into chart-ready points.
 *
 * @returns {{
 *   data: Array<Object>,        // one entry per x, series values keyed by series name
 *   seriesKeys: string[],       // draw order, heaviest first, OTHER last
 *   foldedSeries: number,       // series folded into OTHER
 *   foldedCategories: number,   // x categories folded into OTHER
 *   collapsedDims: string[],    // groupBy dims not shown on either axis
 *   totals: {attempts, approved, value},
 * }}
 */
export function aggregateForChart({
  rows = [],
  groupBy = [],
  xDim,
  seriesDim = null,
  measure = "auth_rate",
  dateDims = new Set(),
  maxSeries = 10,
  maxCategories = 25,
}) {
  const empty = {
    data: [],
    seriesKeys: [],
    foldedSeries: 0,
    foldedCategories: 0,
    collapsedDims: [],
    totals: { attempts: 0, approved: 0, value: null },
  };
  if (!xDim || !rows.length) return empty;

  const isDateX = dateDims.has(xDim);
  const single = !seriesDim || seriesDim === xDim;
  const seriesName = (r) => (single ? measureLabel(measure) : String(r[seriesDim] ?? ""));

  // Any dimension not on an axis is being collapsed away. Callers surface this —
  // a chart that silently drops 4 of 6 grouped dimensions is a chart that lies.
  const collapsedDims = groupBy.filter((k) => k !== xDim && k !== seriesDim);

  // 1. Sum the ADDITIVE measures per (x, series). Rates are never touched here.
  const cells = new Map(); // x -> Map(series -> {attempts, approved})
  const xTotals = new Map(); // x -> attempts
  const seriesTotals = new Map(); // series -> attempts

  for (const r of rows) {
    const x = String(r[xDim] ?? "");
    const s = seriesName(r);
    const attempts = num(r.attempts);
    const approved = num(r.approved);

    if (!cells.has(x)) cells.set(x, new Map());
    const row = cells.get(x);
    const prev = row.get(s) || { attempts: 0, approved: 0 };
    row.set(s, { attempts: prev.attempts + attempts, approved: prev.approved + approved });

    xTotals.set(x, (xTotals.get(x) || 0) + attempts);
    seriesTotals.set(s, (seriesTotals.get(s) || 0) + attempts);
  }

  // 2. Rank and cap. Ranking on attempts (RANK_BY), never on the displayed measure.
  const rankDesc = (m) => [...m.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))).map(([k]) => k);

  let keptSeries = rankDesc(seriesTotals);
  let foldedSeries = 0;
  if (!single && keptSeries.length > maxSeries) {
    foldedSeries = keptSeries.length - maxSeries;
    keptSeries = keptSeries.slice(0, maxSeries);
  }
  const keptSeriesSet = new Set(keptSeries);

  // A date axis is never capped and never reordered by volume — it is a timeline.
  // Gaps are left as gaps: a missing day is missing data, matching "Days with data".
  let keptX;
  let foldedCategories = 0;
  if (isDateX) {
    keptX = [...cells.keys()].sort();
  } else {
    const ranked = rankDesc(xTotals);
    if (ranked.length > maxCategories) {
      foldedCategories = ranked.length - maxCategories;
      keptX = ranked.slice(0, maxCategories);
    } else {
      keptX = ranked;
    }
  }
  const keptXSet = new Set(keptX);

  // 3. Build points. Folding into OTHER SUMS the additive counts and RECOMPUTES the
  //    rate — the single most important line in this file.
  const foldX = new Map(); // series -> {attempts, approved}  (the folded-x bucket)
  const data = [];

  const accumulate = (target, s, cell) => {
    const prev = target.get(s) || { attempts: 0, approved: 0 };
    target.set(s, {
      attempts: prev.attempts + cell.attempts,
      approved: prev.approved + cell.approved,
    });
  };

  for (const x of [...cells.keys()]) {
    const row = cells.get(x);
    const inKeptX = keptXSet.has(x);
    const target = inKeptX ? new Map() : foldX;

    for (const [s, cell] of row.entries()) {
      accumulate(target, keptSeriesSet.has(s) || single ? s : OTHER, cell);
    }
    if (inKeptX) data.push([x, target]);
  }
  if (foldX.size) data.push([OTHER, foldX]);

  const seriesKeys = [...keptSeries.filter((s) => keptSeriesSet.has(s))];
  if (foldedSeries > 0) seriesKeys.push(OTHER);

  // Preserve the intended x order (chronological, or volume-ranked with OTHER last).
  const xOrder = [...keptX, ...(foldX.size ? [OTHER] : [])];
  const byX = new Map(data);

  const points = xOrder
    .filter((x) => byX.has(x))
    .map((x) => {
      const target = byX.get(x);
      const point = { x };
      // __cells carries attempts/approved per series so a tooltip can show the n
      // behind a rate. A rate on 3 attempts is noise; making n always visible is
      // cheaper and more honest than a sample-floor control.
      const cellsOut = {};
      let xAttempts = 0;
      let xApproved = 0;
      for (const s of seriesKeys) {
        const cell = target.get(s);
        if (!cell) continue;
        point[s] = cellValue(cell, measure);
        cellsOut[s] = { attempts: cell.attempts, approved: cell.approved, rate: rate(cell.attempts, cell.approved) };
        xAttempts += cell.attempts;
        xApproved += cell.approved;
      }
      point.__cells = cellsOut;
      point.__attempts = xAttempts;
      point.__approved = xApproved;
      // The x-level rate, recomputed across its series — used by the combo chart's
      // volume bars and by a single-series rate line.
      point.__rate = rate(xAttempts, xApproved);
      return point;
    });

  const totalAttempts = [...xTotals.values()].reduce((a, b) => a + b, 0);
  const totalApproved = rows.reduce((a, r) => a + num(r.approved), 0);

  return {
    data: points,
    seriesKeys,
    foldedSeries,
    foldedCategories,
    collapsedDims,
    totals: {
      attempts: totalAttempts,
      approved: totalApproved,
      value:
        measure === "attempts"
          ? totalAttempts
          : measure === "approved"
          ? totalApproved
          : rate(totalAttempts, totalApproved),
    },
  };
}

/** Donut input: one slice per x, additive measures only (enforced by capability). */
export function toDonutSlices({ data, measure }) {
  if (!isAdditive(measure)) return [];
  return data
    .map((p) => ({
      name: p.x,
      value: measure === "approved" ? p.__approved : p.__attempts,
      attempts: p.__attempts,
      approved: p.__approved,
      rate: p.__rate,
    }))
    .filter((s) => s.value > 0);
}
